/**
 * Password-login constants (plan §2.2.8 AD-2 · ADR-0027).
 *
 * Two values, no functions: both are cross-boundary contracts, and a module
 * that only states them can be imported by the form, by its test and by
 * anything later without dragging behaviour along.
 *
 * `PASSWORD_MIN_LENGTH` is RE-EXPORTED from `@meridian/contracts`, which is now
 * the single definition the API's `assertPasswordPolicy` also enforces. It used
 * to be an independent literal here, bound to the server's by nothing at all —
 * `web → api` is a forbidden edge, so raising the server floor left BOTH suites
 * green while this form kept accepting passwords the API rejects, burning
 * ADR-0011 ban slots on a correct password. The re-export keeps this module the
 * one import site for the form without keeping a second copy of the number.
 * The client check exists to spend a keystroke instead of a round trip and a
 * rate-limit slot; the server remains the authority.
 *
 * `PASSWORD_LOGIN_USERNAME` is the AD-2 password-manager vault key — the handle
 * a browser files the saved credential under, not an account identifier (the
 * wire body carries `{ password }` only).
 *
 * HIGH RISK: changing this value later silently re-keys every saved entry. The
 * manager keeps offering the old key, finds no match for the new one, and the
 * user sees "autofill stopped working" — no error, no log line, nothing on the
 * wire. Treat it as frozen. It is never written as a source literal at the
 * input either: the input reads this constant, so there is exactly one place
 * the value could ever be changed from.
 */

export { PASSWORD_MIN_LENGTH, PASSWORD_MAX_LENGTH } from '@meridian/contracts';

/*
 * A plain literal, deliberately.
 *
 * This line trips `scripts/secret-scan.sh` on its IDENTIFIER — the assignment
 * arm matches any name containing "password" followed by 8+ characters of
 * value — even though a vault key is not a credential. That false positive is
 * answered where it belongs, by a line-exact allow-arm in the scanner, rather
 * than by splitting the literal here. Source that hides from the gate is
 * invisible to the review that should catch it, and the previous shape had
 * already begun to spread as a general recipe for getting a string past it.
 *
 * There is no module-scope value guard either: `password-constants.test.ts`
 * asserts the literal, so a guard here would verify nothing the suite does not
 * already verify, while adding a failure mode that blanks the whole SPA —
 * including the sign-in path — if it ever fired.
 */
export const PASSWORD_LOGIN_USERNAME = 'meridian';
